import { api, API_BASE_URL, ApiError } from './client';
import type { MessagePage } from './client';

export interface ParticipantView {
  personId: string;
  displayName: string;
  classification: 'Youth' | 'Adult' | 'Unknown';
  mode: 'Active' | 'Monitor';
  /** True when they are here because of their role in the unit, not a stored row. */
  derived: boolean;
  /** When somebody put them here, and who. Absent for a derived person -- nobody decided. */
  addedAt?: string | null;
  addedBy?: string | null;
}

/**
 * Somebody who was in the thread and is not any more (TROOP-178).
 *
 * Shown to everybody who can read the thread, youth included -- rule 7 asks that no change to who
 * can read go undisclosed, and a list that quietly loses somebody is the same class of problem as
 * one that quietly gains a hidden observer. Their messages are untouched and still carry their
 * name (rule 6), so withholding this would leave a stream with an author nobody on the page can
 * account for.
 *
 * Not a `ParticipantView`: they are not present and must never reach the participant strip.
 */
export interface FormerParticipantView {
  personId: string;
  displayName: string;
  classification: 'Youth' | 'Adult' | 'Unknown';
  addedAt: string;
  removedAt: string;
  removedBy?: string | null;
}

/**
 * Somebody who can read the thread without being in it — a guardian, under I4.
 *
 * Carries the id as well as the name, and not for styling: names collide, and keying a list on one
 * is wrong the first time a unit has two Emmas. Deliberately not a `ParticipantView` — these people
 * are not present, do not count toward the two-adult quorum, and must never be rendered into the
 * participant row.
 */
export interface GuardianReaderView {
  personId: string;
  displayName: string;
}

/**
 * Somebody who read this thread under I3 without being in it — TROOP-14.
 *
 * Rule 7 is why this reaches the client at all: an oversight read nobody in the thread can see is
 * the covert access the rule forbids. It is rendered to everybody who can read the thread, youth
 * included, exactly as `formerParticipants` is — and, like that list, it is uncomfortable on
 * purpose.
 *
 * One entry per reader rather than per read. The server logs every read, because each is real;
 * `times` is the count over those rows, so a panel says "4 times, most recently Tuesday" instead of
 * four identical lines.
 */
export interface OversightReadView {
  personId: string;
  displayName: string;
  lastReadAt: string;
  times: number;
}

export interface ConversationDetail {
  id: string;
  title: string;
  type: string;
  audienceMode: 'RosterScoped' | 'Explicit';
  audienceIncludes: AudienceIncludes;
  participants: ParticipantView[];
  /** Guardians who can read but are not participants. Disclosed, never hidden. */
  guardianReaders: GuardianReaderView[];
  complianceState: 'Satisfied' | 'NotApplicable' | 'Blocked';
  adultCount: number;
  youthCount: number;
  /**
   * Why *you* may read this thread, from `ConversationAccessPolicy`. A fact about the viewer
   * rather than about the thread, and the thing that decides whether a composer renders:
   * `Participant` and `Monitor` are in the room and may speak, `Guardian` is I4 -- entitled to
   * look in, never to join in (TROOP-67).
   */
  yourBasis?: AccessBasis | null;
  /**
   * The event or announcement this thread belongs to, when it belongs to one.
   *
   * Named `anchoredTo` rather than `anchor` because a list row's `anchor` is a string. Reading it
   * tells you what the thread is about; it does not tell you the reader may open the event, which
   * `EventAccessPolicy` decides when they follow the link.
   */
  anchoredTo?: ConversationAnchorTarget | null;
  /**
   * How far this viewer has been shown, or null if they have never opened the thread.
   *
   * A fact about the viewer, like `yourBasis`. Null is not zero: zero means the marker sits before
   * the first message, null means it was never written. Both mean everything is new today.
   */
  yourLastReadSeq?: number | null;
  /**
   * When this thread was taken off the lists, or null while it is still on them.
   *
   * Off the lists, not gone: it is still readable and still says who is in it. A reader who
   * followed a link here should be told why it is quiet rather than left to wonder.
   */
  archivedAt?: string | null;
  /** Closed to new messages. Separate from archiving — a cancelled event's thread is one and not the other. */
  isLocked?: boolean;
  /** Who has been removed, most recent first. Empty on almost every thread, never absent. */
  formerParticipants?: FormerParticipantView[];
  /**
   * Whether *you* may change who is in the thread, from `ConversationAccessPolicy` (TROOP-178).
   *
   * Two flags because adding and removing are different rules: any member of an explicit thread
   * may add, and only an adult officer may remove. A single `canManage` would have to pick the
   * wrong one for somebody. Asked per viewer and never hardcoded -- a frozen flag fails *silent*,
   * taking the control away from the person who does have the right with nothing to diagnose.
   */
  canAddParticipants?: boolean;
  canRemoveParticipants?: boolean;
  /** Who has read this thread under I3 without being in it, most recent first. Never null. */
  oversightReads?: OversightReadView[];
}

export interface ConversationAnchorTarget {
  kind: 'Event' | 'Announcement';
  id: string;
  title: string;
  /** The event's start. Null for an announcement, which has no time to show. */
  startsAt: string | null;
  cancelled: boolean;
}

export type AccessBasis = 'Participant' | 'Monitor' | 'Guardian' | 'AdminOversight';

/** Whether this basis carries a voice. One definition, used by every screen that asks. */
export const canSpeak = (basis: AccessBasis | null | undefined) =>
  basis === 'Participant' || basis === 'Monitor';

/**
 * Whether this viewer is looking under I3 rather than because the thread is theirs — TROOP-14.
 *
 * One definition, beside `canSpeak`, so no screen compares against the string itself. It gates
 * three things and each is its own reason: the composer (oversight is a read and nothing more), the
 * read marker (a marker is what puts a thread on a badge, and an admin's badge must not count other
 * people's children's threads), and the banner that tells the admin their read is on the record.
 */
export const isOversight = (basis: AccessBasis | null | undefined) => basis === 'AdminOversight';

/**
 * How much is new, and where.
 *
 * `total` is the badge on the whole surface; `byGroup` badges the sections of the conversations
 * page. Neither is a page: a badge summed over the rows a screen happens to have drawn falls when
 * somebody else posts, which looks like a number rather than a bug. Take these from here and never
 * from the list.
 */
export interface UnreadSummary {
  total: number;
  threads: number;
  byGroup: {
    channels: number;
    groups: number;
    otherThreads: number;
    events: number;
    announcements: number;
    direct: number;
  };
}

/**
 * An event's thread, with the facts about the event the page groups and sorts by.
 *
 * The thread is nested rather than flattened so a row that is a thread stays one shape wherever it
 * is rendered -- the section maps it into `ThreadListRow` like every other list does.
 */
export interface EventThreadRow {
  thread: ConversationListRow;
  eventId: string;
  startsAt: string;
  /** The event's own zone, which may not be the unit's and is very rarely the viewer's. */
  timeZoneId: string;
  cancelled: boolean;
  /** The unit's own name for this kind of event. Null when the event has no type. */
  eventTypeName: string | null;
  eventTypeColorKey: string | null;
  /**
   * `yyyy-MM`, in the **event's** zone rather than the reader's.
   *
   * Filed by the server so a late-evening event near a month boundary belongs to the month its
   * unit is in — a parent reading from another zone sees it where their unit put it.
   */
  month: string;
}

/**
 * The events section: a window of whole months, the index of months that hold anything, and the
 * most recently discussed events for the other view.
 *
 * The window is the server's to decide. A unit has thousands of events and `ConversationAccessPolicy`
 * runs per row, so neither the slice nor a month's count can be worked out here.
 */
export interface EventThreadPage {
  /** The month the window actually starts at, which is not always the one asked for. */
  anchor: string | null;
  months: { month: string; count: number }[];
  rows: EventThreadRow[];
  recent: EventThreadRow[];
}

export type AudienceIncludes = 'Everyone' | 'AdultsOnly' | 'YouthAndLeaders' | 'OfficersOnly';

/** What a thread hangs off. The grouping on the conversations page is a projection of this. */
export type ConversationAnchor = 'Standalone' | 'Event' | 'Announcement';

/**
 * The newest message in a thread, for a list row.
 *
 * `body` is null when the message was removed: the tombstone is the product feature and the text is
 * what gets withheld, so a row says who spoke and not what they said. A list must not be the one
 * surface still holding a removed message's words.
 */
export interface MessagePreview {
  authorDisplayName: string;
  body: string | null;
  isRemoved: boolean;
}

/**
 * One thread on a member's list.
 *
 * Everything needed to group and order the list, and nothing that decides who may read it — the
 * sections come from `anchor`, `audienceMode` and `audienceSubgroupId`, which are facts about the
 * thread rather than a discriminator the server computed for one screen.
 */
export interface ConversationListRow {
  id: string;
  title: string;
  type: string;
  audienceMode: 'RosterScoped' | 'Explicit';
  audienceIncludes: AudienceIncludes;
  anchor: ConversationAnchor;
  audienceSubgroupId: string | null;
  /** The unit's own word for the group, never "patrol" (rule 16). */
  subgroupName: string | null;
  /**
   * One of the channels the unit was created with, rather than a thread somebody started.
   *
   * Ordering and grouping only -- it grants nothing. A whole-roster thread an officer starts is
   * the same shape as the whole-unit channel, so without this the section built to show the unit's
   * channels shows whatever else happens to be roster-scoped. They are told apart by their names,
   * which is what makes both worth listing.
   */
  isStandingChannel: boolean;
  lastMessageSeq: number;
  /** When the thread last moved. What the list is ordered by, and what a row shows. */
  lastMessageAt: string | null;
  preview: MessagePreview | null;
  /** Messages not yet shown to me. Private to me — nobody sees anyone else's. */
  unreadCount: number;
}

/** Carries the domain rule that was violated, so the UI can render the right refusal. */
export class ComplianceRefusal extends Error {
  constructor(
    public readonly title: string,
    message: string,
    public readonly rule?: string,
  ) {
    super(message);
    this.name = 'ComplianceRefusal';
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string; rule?: string } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    if (response.status === 409 && problem.title) {
      throw new ComplianceRefusal(problem.title, problem.detail ?? '', problem.rule);
    }

    throw new ApiError(response.status, problem.detail ?? problem.title ?? `Failed (${response.status}).`);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/**
 * One thread on a parent's list, and why it is there.
 *
 * `basis` is the domain's own answer rather than something the screen works out: `Guardian` is I4
 * -- they can read it because their child is in it -- and `Participant`/`Monitor` means they are in
 * it in their own right. Rendering the reason matters here more than anywhere else in the product,
 * because a parent reading a thread they are not in is exactly the access this page exists to give
 * them, and it should never look like eavesdropping.
 */
export interface FamilyConversationRow {
  id: string;
  unitId: string;
  unitName: string;
  title: string;
  type: string;
  audienceMode: 'RosterScoped' | 'Explicit';
  audienceIncludes: AudienceIncludes;
  anchor: ConversationAnchor;
  audienceSubgroupId: string | null;
  subgroupName: string | null;
  isStandingChannel: boolean;
  basis: AccessBasis;
  /** Which of this parent's own children are in it. Empty on the parents-and-leaders channel. */
  childNames: string[];
  lastMessageSeq: number;
  lastMessageAt: string | null;
  preview: MessagePreview | null;
  unreadCount: number;
}

/**
 * How a screen reaches one thread.
 *
 * Two routes serve the same conversations to different people: a member reaches them under
 * `/units/{unitId}/`, and a parent -- who holds no membership and would be 404'd there -- reaches
 * them under `/family/`. The shapes are identical, so `useConversation` takes one of these rather
 * than growing a branch.
 *
 * Both now carry `post`. It was absent on the family side until TROOP-67, when a parent stopped
 * being a mere reader of a roster-scoped thread and became a participant of it. What decides
 * whether a composer appears is no longer which transport a screen holds -- it is the viewer's
 * `yourBasis` on the thread, because the same parent may speak in the unit thread and may not
 * speak in a Direct thread they read under I4.
 */
export interface ConversationTransport {
  getMessages: (conversationId: string, after?: number, limit?: number) => Promise<MessagePage>;
  markRead: (conversationId: string, seq: number) => Promise<{ lastReadSeq: number }>;
  post?: (conversationId: string, body: string, clientMessageId: string) => Promise<unknown>;
}

export const unitTransport = (unitId: string, oversight = false): ConversationTransport => ({
  getMessages: (conversationId, after, limit) =>
    api.getMessages(unitId, conversationId, after, limit, oversight),
  markRead: (conversationId, seq) => conversationsApi.markRead(unitId, conversationId, seq),
  post: (conversationId, body, clientMessageId) =>
    api.postMessage(unitId, conversationId, body, clientMessageId),
});

export const familyTransport: ConversationTransport = {
  getMessages: (conversationId, after, limit) => familyConversationsApi.messages(conversationId, after, limit),
  markRead: (conversationId, seq) => familyConversationsApi.markRead(conversationId, seq),
  post: (conversationId, body, clientMessageId) =>
    familyConversationsApi.post(conversationId, body, clientMessageId),
};

/**
 * A parent's way in, outside `/units/` for the reason the whole `/family` group is: guardians hold
 * no membership, so a unit-scoped route 404s the very person it exists for. The API decides who may
 * read what with the same policy the unit routes use -- these are a different door, not a different
 * rule.
 */
/**
 * One page of a conversation list, plus where to resume.
 *
 * `nextCursor` is opaque and is only ever echoed back as `after`. Null means the walk reached the
 * end -- so "no cursor" is the stop condition, not an empty page, though an empty page is also
 * safe to stop on.
 */
export interface ConversationPage<T> {
  rows: T[];
  nextCursor: string | null;
}

export const familyConversationsApi = {
  /**
   * Paged, most recently active first (TROOP-63). This list spans every unit a parent has a child
   * in, so it is the one that grew fastest -- 1,393 threads for one parent before it was bounded.
   */
  list: (after?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (after) params.set('after', after);
    if (limit !== undefined) params.set('limit', String(limit));
    const query = params.toString();
    return call<ConversationPage<FamilyConversationRow>>(
      `/family/conversations${query ? `?${query}` : ''}`,
    );
  },

  /**
   * The badge over every thread a parent may read, and how many there are -- neither of which the
   * paged list can answer, because it only ever holds one page. Both come off one authorization
   * pass on the server, so asking for them together costs no more than asking for either.
   */
  /**
   * The channels across every unit this parent has a child in, unpaged.
   *
   * The twin of the unit call, and separate from `list` for the same reason: a channel nobody has
   * posted in has no last-message time, so it sorts behind every thread anybody has -- and a
   * parent's feed spans units, so it is the longest one in the product.
   */
  channels: () => call<{ rows: FamilyConversationRow[] }>('/family/conversations/channels'),

  /** The twin of the unit call, over the units this parent's children are in. */
  events: (anchor?: string) =>
    call<EventThreadPage>(`/family/conversations/events${anchor ? `?anchor=${anchor}` : ''}`),

  unreadTotal: () => call<UnreadSummary>('/family/conversations/unread'),

  detail: (conversationId: string) =>
    call<ConversationDetail>(`/family/conversations/${conversationId}`),

  messages: (conversationId: string, after?: number, limit = 50) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (after !== undefined) {
      params.set('after', String(after));
    }
    return call<MessagePage>(`/family/conversations/${conversationId}/messages?${params}`);
  },

  markRead: (conversationId: string, seq: number) =>
    call<{ lastReadSeq: number }>(`/family/conversations/${conversationId}/read`, {
      method: 'PUT',
      body: JSON.stringify({ seq }),
    }),

  /**
   * The API refuses this with a 403 when the basis is `Guardian`, so a screen that renders a
   * composer it should not have is a cosmetic bug rather than a hole. The server decides.
   */
  post: (conversationId: string, body: string, clientMessageId: string) =>
    call<{ id: string; seq: number; postedAt: string; duplicate: boolean }>(
      `/family/conversations/${conversationId}/messages`,
      { method: 'POST', body: JSON.stringify({ body, clientMessageId }) },
    ),
};

/**
 * One of the channels a unit is offered, and whether it has it.
 *
 * `unavailable` is not the same as off. Off is a choice the unit made; unavailable is the
 * invariants refusing — a single-adult unit cannot staff the youth channel. The screen offers a
 * toggle for the first and the reason for the second.
 */
export interface SuggestedChannel {
  audience: AudienceIncludes;
  title: string;
  description: string;
  enabled: boolean;
  unavailable: boolean;
  reason: string | null;
  conversationId: string | null;
}

/** Which standing channels a unit wants. Officer-only, both ways. */
export const channelSettingsApi = {
  suggested: (unitId: string) =>
    call<{ rows: SuggestedChannel[] }>(`/units/${unitId}/conversations/channels/suggested`),

  /** Returns the whole set back, so the screen never has to guess what changed. */
  setEnabled: (unitId: string, audience: string, enabled: boolean) =>
    call<{ rows: SuggestedChannel[] }>(
      `/units/${unitId}/conversations/channels/suggested/${audience}`,
      { method: 'PUT', body: JSON.stringify({ enabled }) },
    ),
};

export const conversationsApi = {
  /** Paged, most recently active first (TROOP-63). */
  list: (unitId: string, after?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (after) params.set('after', after);
    if (limit !== undefined) params.set('limit', String(limit));
    const query = params.toString();
    return call<ConversationPage<ConversationListRow>>(
      `/units/${unitId}/conversations/${query ? `?${query}` : ''}`,
    );
  },

  /**
   * The unit's channels: standing threads and one per group, unpaged.
   *
   * A separate call from `list` on purpose, and not an optimisation to fold back in. The list is a
   * recency feed, and a channel nobody has posted in has no last-message time at all -- so it sorts
   * behind every thread anybody has ever posted in, which on a real unit's first day is *all* of
   * its channels. The section that exists to show them would be empty on the screen built for it.
   */
  channels: (unitId: string) =>
    call<{ rows: ConversationListRow[] }>(`/units/${unitId}/conversations/channels`),

  /**
   * Event threads, discussed ones first by recency and then upcoming ones by when they happen.
   *
   * Separate from `list` for the reason `channels` is: an event nobody has posted about yet has no
   * last-message time, so on the feed it sorts behind every thread anybody has ever posted in --
   * which is every event a leader scheduled this week. Both sort keys are on the row, so the
   * screen's sort toggle needs no second request.
   */
  events: (unitId: string, anchor?: string) =>
    call<EventThreadPage>(
      `/units/${unitId}/conversations/events${anchor ? `?anchor=${anchor}` : ''}`,
    ),

  /**
   * @param oversight I3 — TROOP-14. Absent, a unit admin who is not in the thread gets the same 404
   * as anybody else, which is what makes this safe to call on every thread. Present, the server
   * writes a `ConversationAccessLog` row before answering and the thread discloses it to everybody
   * in it. Never set speculatively: it is set after the admin has been told what it does.
   */
  detail: (unitId: string, conversationId: string, oversight = false) =>
    call<ConversationDetail>(
      `/units/${unitId}/conversations/${conversationId}${oversight ? '?oversight=true' : ''}`,
    ),

  /**
   * Puts somebody in the thread (TROOP-178).
   *
   * The server runs every invariant on the result -- the two-adult quorum, a Pack refusing youth,
   * somebody with no standing in the unit -- and each refusal arrives with a sentence to render.
   * The screen is not the enforcement point and must not pre-judge any of them.
   */
  addParticipant: (
    unitId: string,
    conversationId: string,
    personId: string,
    mode: 'Active' | 'Monitor' = 'Active',
  ) =>
    call<{ added: boolean }>(
      `/units/${unitId}/conversations/${conversationId}/participants`,
      { method: 'POST', body: JSON.stringify({ personId, mode }) },
    ),

  /**
   * Takes somebody out. Nothing is deleted -- their messages stay, attributed to them, and the
   * panel keeps them under "No longer here" (rules 6 and 7).
   *
   * A 409 means they are in the thread but have no row to remove: the roster puts them there, or
   * their own child does. That is not a failure to work around, and the message says what would
   * actually change it.
   */
  removeParticipant: (unitId: string, conversationId: string, personId: string) =>
    call<{ removed: boolean }>(
      `/units/${unitId}/conversations/${conversationId}/participants/${personId}`,
      { method: 'DELETE' },
    ),

  /**
   * Takes a thread off the lists, or puts it back.
   *
   * `lock` is separate from `archived` on purpose: retiring a channel usually wants both, and an
   * event being called off wants only the first — the conversation about a cancellation is the
   * thing people need in the hour after it happens.
   */
  setArchived: (unitId: string, conversationId: string, archived: boolean, lock = false) =>
    call<{ archivedAt: string | null; isLocked: boolean }>(
      `/units/${unitId}/conversations/${conversationId}/archived`,
      { method: 'PUT', body: JSON.stringify({ archived, lock }) },
    ),

  /**
   * Records how far this person has actually been shown. Monotonic and clamped server-side, so
   * the returned value is the one to believe rather than the one that was sent.
   */
  markRead: (unitId: string, conversationId: string, seq: number) =>
    call<{ lastReadSeq: number }>(`/units/${unitId}/conversations/${conversationId}/read`, {
      method: 'PUT',
      body: JSON.stringify({ seq }),
    }),

  unreadTotal: (unitId: string) => call<UnreadSummary>(`/units/${unitId}/unread`),

  create: (
    unitId: string,
    input: {
      title: string;
      audienceMode: 'RosterScoped' | 'Explicit';
      audienceIncludes: 'Everyone' | 'AdultsOnly';
      /**
       * Narrows a roster-scoped thread to one of the unit's groups. The thread then contains that
       * group's members plus the unit's adult officers as monitors, which is what makes a patrol
       * thread compliant without anybody adding an adult.
       */
      audienceSubgroupId?: string;
      participantIds?: string[];
    },
  ) => call<{ conversationId: string }>(`/units/${unitId}/conversations/`, {
    method: 'POST',
    body: JSON.stringify(input),
  }),
};
